import { defineRecipe } from '@chakra-ui/react';
import { buttonRecipe as kdsButtonRecipe } from '@kintsugi/ui/recipes';

const kdsVariants = kdsButtonRecipe.variants!;

/**
 * Admin still has call sites on colorPalettes outside the KDS set (blue, green,
 * orange, …). KDS compoundVariants only paint brand/neonGreen/neutral/red; without
 * a colorPalette.* fallback those solids/outlines render with no fill/border.
 */
const solidWithPaletteFallback = {
  ...kdsVariants.variant!.solid,
  bg: 'colorPalette.solid',
  color: 'colorPalette.contrast',
  _hover: { bg: 'colorPalette.solid', opacity: 0.9 },
  _active: { bg: 'colorPalette.solid', opacity: 0.8 },
};

const outlineWithPaletteFallback = {
  ...kdsVariants.variant!.outline,
  borderWidth: '1px',
  borderColor: 'colorPalette.solid',
  color: 'colorPalette.fg',
  _hover: { bg: 'colorPalette.subtle', borderColor: 'colorPalette.solid' },
  _active: { bg: 'colorPalette.muted', borderColor: 'colorPalette.solid' },
};

const ghostWithPaletteFallback = {
  ...kdsVariants.variant!.ghost,
  color: 'colorPalette.fg',
  _hover: { bg: 'colorPalette.subtle' },
  _active: { bg: 'colorPalette.muted' },
};

/** Sidebar LinkButton keeps pre-KDS label type — size="sm" must not pull small.medium. */
const navTypographyReset = {
  fontSize: 'sm',
  fontWeight: 'normal',
  lineHeight: 'short',
  letterSpacing: 'normal',
};

/**
 * Admin button recipe: KDS Button styles, plus a `nav` variant used by
 * LinkButton in the sidebar. KDS explicitly excludes sidebar nav from the
 * button recipe (future NavItem); until that lands, keep `nav` here.
 */
export const buttonRecipe = defineRecipe({
  className: kdsButtonRecipe.className,
  base: kdsButtonRecipe.base,
  // Name layout/size explicitly — a bare spread + `variant` override loses those
  // keys in defineRecipe inference, so KDS compoundVariants/defaultVariants fail tsc.
  variants: {
    layout: kdsVariants.layout,
    size: kdsVariants.size,
    variant: {
      solid: solidWithPaletteFallback,
      outline: outlineWithPaletteFallback,
      ghost: ghostWithPaletteFallback,
      nav: {
        ...navTypographyReset,
        width: '100%',
        height: '44px',
        justifyContent: 'flex-start',
        borderRadius: '8px',
        px: '2.5',
        bg: 'transparent',
        color: 'neutral.0',
        '&[data-expanded="false"]': {
          minWidth: '45px',
          width: '45px',
          alignSelf: 'center',
        },
        _hover: {
          bg: 'neutral.0/10',
        },
        _active: {
          bg: 'neutral.0/10',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: '10px',
            bottom: 0,
            borderRadius: '4px',
            width: '4px',
            height: '24px',
            background: 'neonGreen.500',
          },
        },
      },
    },
  },
  compoundVariants: [
    ...(kdsButtonRecipe.compoundVariants ?? []),
    // Size variants set h/minW/px/textStyle after `variant`; re-assert nav chrome + type so they win.
    {
      variant: 'nav',
      css: {
        ...navTypographyReset,
        height: '44px',
        minW: '0',
        px: '2.5',
      },
    },
  ],
  defaultVariants: kdsButtonRecipe.defaultVariants,
});
